import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Select,
  Button,
  Space,
  Table,
  Alert,
  Tag,
  Row,
  Col,
  Progress,
  Empty,
  Tooltip,
  Spin,
  Typography,
  theme,
} from 'antd';
import {
  TeamOutlined,
  RiseOutlined,
  InfoCircleOutlined,
  TrophyOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'store';
import { compareSegments, selectSegmentComparison, selectLoadingFor } from 'store/slices/stats.slice';
import { pushApi } from 'api/endpoints/push.api';
import { Segment } from 'types/push.types';
import { AppStatsCard } from './AppStatsCard';
import { extractList, leaderBy, maxBy } from './compareHelpers';
import styles from '../../pages/Statistics/statistics.module.css';

const { Text } = Typography;

const MIN_SELECTED = 2;
const MAX_SELECTED = 5;

const PERIOD_OPTIONS = [
  { value: '7d', label: 'Последние 7 дней' },
  { value: '30d', label: 'Последние 30 дней' },
  { value: '90d', label: 'Последние 90 дней' },
];

interface CompareSegmentsProps {
  appId: number;
}

const usersOf = (row: any) => row.user_count ?? 0;
const ctrOf = (row: any) => row.performance?.avg_ctr ?? 0;
const conversionOf = (row: any) => row.performance?.conversion_rate ?? 0;

const conversionTagColor = (rate: number) => {
  if (rate >= 10) return 'success';
  if (rate >= 5) return 'processing';
  return 'default';
};

export const CompareSegments: React.FC<CompareSegmentsProps> = ({ appId }) => {
  const dispatch = useAppDispatch();
  const { token } = theme.useToken();
  const comparison = useAppSelector(selectSegmentComparison);
  const comparing = useAppSelector(selectLoadingFor('segmentComparison'));

  const [selected, setSelected] = useState<number[]>([]);
  const [period, setPeriod] = useState<string>('30d');
  const [segments, setSegments] = useState<Segment[]>([]);
  const [listLoading, setListLoading] = useState(false);

  useEffect(() => {
    if (!appId) return undefined;

    let cancelled = false;
    setSelected([]);
    setListLoading(true);

    pushApi
      .listSegments(appId)
      .then((response) => {
        if (!cancelled) setSegments(extractList<Segment>(response));
      })
      .catch(() => {
        if (!cancelled) setSegments([]);
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [appId]);

  const handleCompare = () => {
    if (selected.length < MIN_SELECTED || selected.length > MAX_SELECTED) return;
    dispatch(compareSegments({ app_id: appId, segment_ids: selected, period }));
  };

  const segmentOptions = useMemo(
    () =>
      segments.map((segment) => ({
        value: segment.id,
        label: `${segment.name} (${(segment.user_count ?? 0).toLocaleString('ru-RU')})`,
      })),
    [segments],
  );

  const rows = comparison?.segments ?? [];
  const bestUsers = maxBy(rows, usersOf);
  const bestCtr = maxBy(rows, ctrOf);
  const bestConversion = maxBy(rows, conversionOf);
  const leader = leaderBy(rows, conversionOf);
  const totalUsers = rows.reduce((sum: number, row: any) => sum + usersOf(row), 0);

  const bestMark = (isBest: boolean) =>
    isBest ? <TrophyOutlined style={{ marginLeft: 4, color: token.colorPrimary }} /> : null;

  const columns = [
    {
      title: 'Сегмент',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left' as const,
      width: 200,
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Пользователи',
      key: 'user_count',
      width: 140,
      sorter: (a: any, b: any) => usersOf(a) - usersOf(b),
      render: (_: unknown, record: any) => {
        const value = usersOf(record);
        return (
          <Text strong={value === bestUsers}>
            {value.toLocaleString('ru-RU')}
            {bestMark(value === bestUsers)}
          </Text>
        );
      },
    },
    {
      title: 'Push',
      dataIndex: 'push_count',
      key: 'push_count',
      width: 100,
      sorter: (a: any, b: any) => (a.push_count ?? 0) - (b.push_count ?? 0),
      render: (count: number) => <Text>{(count ?? 0).toLocaleString('ru-RU')}</Text>,
    },
    {
      title: 'Средний CTR',
      key: 'avg_ctr',
      width: 150,
      sorter: (a: any, b: any) => ctrOf(a) - ctrOf(b),
      render: (_: unknown, record: any) => {
        const value = ctrOf(record);
        return (
          <div>
            <Text strong={value === bestCtr} style={{ fontSize: 12 }}>
              {value.toFixed(2)}%
              {bestMark(value === bestCtr)}
            </Text>
            <Progress
              percent={Math.min(value * 10, 100)}
              showInfo={false}
              size="small"
              strokeColor={token.colorPrimary}
            />
          </div>
        );
      },
    },
    {
      title: 'Конверсия',
      key: 'conversion_rate',
      width: 130,
      sorter: (a: any, b: any) => conversionOf(a) - conversionOf(b),
      render: (_: unknown, record: any) => {
        const value = conversionOf(record);
        return (
          <span>
            <Tag color={conversionTagColor(value)}>{value.toFixed(2)}%</Tag>
            {bestMark(value === bestConversion)}
          </span>
        );
      },
    },
  ];

  return (
    <Card
      title={
        <span className={styles.chartTitle}>
          <div className={styles.chartTitleIcon}>
            <TeamOutlined />
          </div>
          Сравнение сегментов
        </span>
      }
      className={styles.statsCard}
      extra={
        <Tooltip title={`Можно сравнить от ${MIN_SELECTED} до ${MAX_SELECTED} сегментов`}>
          <InfoCircleOutlined />
        </Tooltip>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} md={12}>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
              Сегменты для сравнения
            </Text>
            <Select
              mode="multiple"
              value={selected}
              onChange={setSelected}
              options={segmentOptions}
              optionFilterProp="label"
              style={{ width: '100%' }}
              placeholder={listLoading ? 'Загрузка сегментов...' : 'Выберите сегменты'}
              className={styles.modernSelect}
              maxTagCount={MAX_SELECTED}
              allowClear
              size="small"
              loading={listLoading}
              notFoundContent={
                listLoading ? (
                  <Spin size="small" />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет сегментов" />
                )
              }
            />
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
              Период анализа
            </Text>
            <Select
              value={period}
              onChange={setPeriod}
              options={PERIOD_OPTIONS}
              style={{ width: '100%' }}
              className={styles.modernSelect}
              size="small"
            />
          </Col>
          <Col xs={24} md={6}>
            <Button
              type="primary"
              size="small"
              block
              onClick={handleCompare}
              disabled={selected.length < MIN_SELECTED || selected.length > MAX_SELECTED}
              loading={comparing}
              icon={<BarChartOutlined />}
              className={styles.modernButton}
            >
              Сравнить сегменты
            </Button>
          </Col>
        </Row>

        {selected.length > 0 && selected.length < MIN_SELECTED && (
          <Alert
            message={`Выберите минимум ${MIN_SELECTED} сегмента`}
            type="info"
            showIcon
            className={styles.modernAlert}
          />
        )}

        {rows.length > 0 ? (
          <>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={8}>
                <AppStatsCard
                  title="Всего пользователей"
                  value={totalUsers}
                  icon={<TeamOutlined />}
                  className={styles.info}
                  compact
                />
              </Col>
              <Col xs={24} sm={8}>
                <AppStatsCard
                  title="Лучший CTR"
                  value={`${(bestCtr ?? 0).toFixed(2)}%`}
                  icon={<RiseOutlined />}
                  className={styles.success}
                  compact
                />
              </Col>
              <Col xs={24} sm={8}>
                <AppStatsCard
                  title="Лучшая конверсия"
                  value={`${(bestConversion ?? 0).toFixed(2)}%`}
                  icon={<TrophyOutlined />}
                  compact
                />
              </Col>
            </Row>

            <Table
              columns={columns}
              dataSource={rows}
              rowKey="id"
              pagination={false}
              scroll={{ x: 800 }}
              size="small"
            />

            {leader && (
              <Alert
                message={`Лучшая конверсия у сегмента «${leader.name}» — ${conversionOf(leader).toFixed(2)}%`}
                type="success"
                showIcon
                className={styles.modernAlert}
              />
            )}
          </>
        ) : (
          <Empty
            description="Выберите сегменты и нажмите «Сравнить»"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}

        {comparison?.warnings?.inaccessible_segments?.length ? (
          <Alert
            message={comparison.warnings.message}
            description={`Недоступные сегменты: ${comparison.warnings.inaccessible_segments.join(', ')}`}
            type="warning"
            showIcon
            className={styles.modernAlert}
          />
        ) : null}
      </Space>
    </Card>
  );
};